import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useState,
} from 'react'
import Slider from 'react-slick'
import Image from 'next/image'
import 'slick-carousel/slick/slick.css'
//import 'slick-carousel/slick/slick-theme.css'
import type { IDiamond } from '../../../types/diamond.types'
import { DiamondCert } from '@components/yadav/ui'
import Link from 'next/link'
import type { ProductVariant, ProductVideo } from '@commerce/types/product'
import cn from 'clsx'
import Asscher from '@components/yadav/icons/shapes/Asscher'
import Cushion from '@components/yadav/icons/shapes/Cushion'
import ElCushion from '@components/yadav/icons/shapes/ElCushion'
import Emerald from '@components/yadav/icons/shapes/Emerald'
import Heart from '@components/yadav/icons/shapes/Heart'
import Marquise from '@components/yadav/icons/shapes/Marquise'
import Oval from '@components/yadav/icons/shapes/Oval'
import Pear from '@components/yadav/icons/shapes/Pear'
import Princess from '@components/yadav/icons/shapes/Princess'
import Radiant from '@components/yadav/icons/shapes/Radiant'
import Round from '@components/yadav/icons/shapes/Round'
import Square from '@components/yadav/icons/shapes/Square'
import { imageLoader } from '@utils/image-loader'

interface CarouselProps {
  dots?: boolean
  arrows?: boolean
  infinite?: boolean
  speed?: number
  slidesToShow?: number
  slidesToScroll?: number
  width: number
  height: number
  data: any
  className?: string
  centerMode?: boolean
  centerPadding?: string
  nextArrow?: JSX.Element
  prevArrow?: JSX.Element
  classDiv?: string
  handleImageError?: Function
  ProductPreview?: FunctionComponent
  diamond?: IDiamond
  swipeToSlide?: boolean
  hasValidVideoUrlFormat?: Function
  setDisplayCertModal?: Dispatch<SetStateAction<boolean>>
  productVariant?: ProductVariant
  from?: string
  onClickShape?: (title: string) => void
}

const Carousel: React.FC<CarouselProps> = ({
  dots = true,
  arrows = true,
  infinite = true,
  speed = 500,
  slidesToShow = 1,
  slidesToScroll = 1,
  width,
  height,
  data,
  centerMode,
  centerPadding,
  nextArrow,
  prevArrow,
  classDiv,
  handleImageError,
  ProductPreview,
  diamond,
  swipeToSlide,
  hasValidVideoUrlFormat,
  setDisplayCertModal,
  productVariant,
  from,
  onClickShape,
}) => {
  const [showVideo, setShowVideo] = useState(false)

  if (data.length === 0) {
    return null
  }

  const settings = {
    dots,
    infinite,
    speed,
    slidesToShow,
    slidesToScroll,
    swipeToSlide,
    arrows,
    centerPadding,
    centerMode,
    nextArrow: nextArrow,
    prevArrow: prevArrow,
    className: '!h-full !flex !flex-col !justify-between',
    dotsClass: 'button__bar',
    appendDots: (dots: any) => (
      <div className="-bottom-5">
        <ul
          style={{
            margin: '0px',
            zIndex: 0,
            display: 'flex',
            marginTop: '4px',
          }}
        >
          {' '}
          {dots}{' '}
        </ul>
      </div>
    ),
  }

  const shapeStyles =
    'fill-gray-dark-2 stroke-[.5px] stroke-gray-dark-2 mb-9 scale-110 pt-2'

  const shapes: { [key: string]: JSX.Element } = {
    Asscher: <Asscher className={shapeStyles} mobile={true} />,
    'Square Cushion': <Cushion className={shapeStyles} mobile={true} />,
    'Elongated Cushion': <ElCushion className={shapeStyles} mobile={true} />,
    Emerald: <Emerald className={shapeStyles} mobile={true} />,
    Heart: <Heart className={shapeStyles} mobile={true} />,
    Marquise: <Marquise className={shapeStyles} mobile={true} />,
    Oval: <Oval className={shapeStyles} mobile={true} />,
    Pear: <Pear className={shapeStyles} mobile={true} />,
    Princess: <Princess className={shapeStyles} mobile={true} />,
    Radiant: <Radiant className={shapeStyles} mobile={true} />,
    Round: <Round className={shapeStyles} mobile={true} />,
    'Square Radiant': <Square className={shapeStyles} mobile={true} />,
  }

  return (
    <div className="h-full">
      {ProductPreview && diamond ? (
        <Slider {...settings}>
          {diamond.videourl &&
            hasValidVideoUrlFormat &&
            hasValidVideoUrlFormat(diamond.videourl) && <ProductPreview />}
          {data.map((image: any, index: number) => (
            <div className={classDiv} key={index}>
              <Image
                loading="lazy"
                loader={imageLoader}
                src={image}
                layout="fill"
                objectFit="contain"
                alt={`image-${index}`}
                onError={handleImageError ? handleImageError() : () => { }}
                quality="85"
                placeholder="blur"
                blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
              />
            </div>
          ))}
          {diamond && diamond.lab && setDisplayCertModal && (
            <DiamondCert
              diamond={diamond}
              key={diamond.id}
              setDisplayCertModal={setDisplayCertModal}
            />
          )}
        </Slider>
      ) : from && from === 'shapesMobile' ? (
        <Slider
          {...settings}
          variableWidth={true}
          adaptiveHeight={true}
          className="slider variable-width"
        >
          {data.map((image: any, index: number) => (
            <div className={classDiv} key={index}>
              {image.link && image.slug ? (
                <Link href={image.slug}>
                  <a onClick={() => onClickShape && onClickShape(image.link)} className='flex flex-col items-center'>
                    {shapes[image.link]}
                    <span
                      className="relative text-sm text-primary z-2"
                    >
                      <p
                        className={`${'Emerald' === image.link
                            ? 'w-[45px] text-center ml-[-4px]'
                            : 'Marquise' === image.link
                              ? 'w-[35px] text-center ml-[-11.625px]'
                              : 'Square Radiant' === image.link
                                ? 'w-[62px] text-center mt-[-10px]'
                                : 'Square Cushion' === image.link
                                  ? 'w-[62px] text-center mt-[-10px]'
                                  : 'Elongated Cushion' === image.link
                                    ? 'w-[72px] text-center mt-[-10px]'
                                    : ''
                          }`}
                      >
                        {image.link}
                      </p>
                    </span>
                  </a>
                </Link>
              ) : image.link && image.slug ? (
                <Link href={image.slug}>
                  <a>
                    <Image
                      loading="lazy"
                      loader={imageLoader}
                      src={image.src ? image.src : image}
                      width={width}
                      height={height}
                      alt={`image-${index}`}
                      layout="responsive"
                      onError={handleImageError ? handleImageError() : () => { }}
                      quality="85"
                      className=" scale-110"
                    />
                    <p className="text-sm text-primary mt-5 mb-12">
                      {image.link}
                    </p>
                  </a>
                </Link>
              ) : (
                <Image
                  loading="lazy"
                  loader={imageLoader}
                  src={image.src ? image.src : image}
                  width={width}
                  height={height}
                  alt={`image-${index}`}
                  layout="responsive"
                  onError={handleImageError ? handleImageError() : () => { }}
                  quality="85"
                />
              )}
            </div>
          ))}
        </Slider>
      ) : (
        <Slider {...settings}>
          {productVariant &&
            productVariant.images &&
            productVariant.images.length > 0 &&
            productVariant.videos &&
            productVariant.videos.length > 0 &&
            (productVariant.videos[0].local_url ||
              productVariant.videos[0].alternative_url) && (
              <div className="relative w-full h-full">
                <div className="absolute top-0 left-0 transform w-[340px] h-[340px]">
                  <Image
                    priority={true}
                    loader={imageLoader}
                    src={productVariant.images[0].url || ''}
                    alt={productVariant.images[0].alt || 'Product Image'}
                    layout="fill"
                    objectFit="contain"
                    className={cn(
                      { visible: !showVideo },
                      { invisible: showVideo }
                    )}
                  />
                  <div className="absolute top-0 left-0 w-full h-[300px]">
                    <video
                      src={
                        productVariant.videos[0].local_url ||
                        productVariant.videos[0].alternative_url ||
                        ''
                      }
                      muted
                      autoPlay
                      loop
                      className={cn(
                        'h-[300px]',
                        { visible: showVideo },
                        { invisible: !showVideo }
                      )}
                      onLoadedData={() => setShowVideo(true)}
                      playsInline
                    >
                      <source
                        src={
                          productVariant.videos[0].local_url ||
                          productVariant.videos[0].alternative_url ||
                          ''
                        }
                        type="video/mp4"
                      />
                    </video>
                  </div>
                </div>
              </div>
            )}
          {data.map((image: any, index: number) => (
            <div className={classDiv} key={index}>
              {image.isVideo ? (
                <div className="relative w-full h-full">
                  <video
                    src={image.src}
                    muted
                    loop
                    controls={false}
                    autoPlay
                    className="w-full h-full object-cover rounded-lg"
                    onClick={(e) => {
                      const videoElement = e.currentTarget;
                      if (videoElement.paused) {
                        videoElement.play();
                      } else {
                        videoElement.pause();
                      }
                    }}
                  >
                    <source src={image.src} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                image.link && image.slug ? (
                  <Link href={image.slug}>
                    <a>
                      <Image
                        loading="lazy"
                        loader={imageLoader}
                        src={image.src ? image.src : image}
                        width={width}
                        height={height}
                        alt={`image-${index}`}
                        layout="responsive"
                        onError={handleImageError ? handleImageError() : () => { }}
                        quality="85"
                        className="scale-110"
                      />
                      <p className="text-sm text-primary mt-5 mb-12">{image.link}</p>
                    </a>
                  </Link>
                ) : (
                  <Image
                    loading="lazy"
                    loader={imageLoader}
                    src={image.src ? image.src : image}
                    width={width}
                    height={height}
                    alt={`image-${index}`}
                    layout="responsive"
                    onError={handleImageError ? handleImageError() : () => { }}
                    quality="85"
                  />
                )
              )}
            </div>
          ))}

        </Slider>
      )}
    </div>
  )
}

export default Carousel
